import { supabase } from '../lib/supabaseClient';
import type { Question } from '../types/generated';
import type { QuestionDB } from '../models/database';

/**
 * Service for Question operations
 * Handles database interactions and mapping to GraphQL types
 */
export const questionService = {
  /**
   * Get a question by its ID
   * 
   * @param id - The unique identifier for the question
   * @returns The question or null if not found
   */
  async getQuestionById(id: string): Promise<Question | null> {
    const { data, error } = await supabase
      .from('questions')
      .select('question_id, header, format, questions, response_schema, organization_id, created_from_question_id')
      .eq('question_id', id)
      .single();
    
    if (error) throw error;
    
    return data ? mapQuestionDBToGraphQL(data) : null;
  },

  /**
   * Get questions for a specific check-in (by template_id)
   * 
   * @param templateId - The ID of the template
   * @returns Array of questions
   */
  async getQuestionsByTemplate(templateId: string): Promise<Question[]> {
    const { data, error } = await supabase
      .from('questions')
      .select('question_id, header, format, questions, response_schema, organization_id, created_from_question_id')
      .eq('template_id', templateId)
      .order('order', { ascending: true }); // If 'order' field exists
    if (error) throw error;
    if (!data || data.length === 0) {
      return [];
    }
    return data.map(mapQuestionDBToGraphQL);
  },

  /**
   * Create a new question
   * 
   * @param questionData - The question data
   * @returns The created question
   */
  async createQuestion(questionData: Omit<QuestionDB, 'question_id'>): Promise<Question> {
    const { data, error } = await supabase
      .from('questions')
      .insert({
        header: questionData.header,
        format: questionData.format,
        questions: questionData.questions,
        response_schema: questionData.response_schema,
        organization_id: questionData.organization_id,
        created_from_question_id: questionData.created_from_question_id
      })
      .select()
      .single();
    
    if (error) throw error;
    
    return mapQuestionDBToGraphQL(data);
  },

  /**
   * Duplicate a global question for an organization
   * 
   * @param questionId - The ID of the global question to duplicate
   * @param organizationId - The organization ID
   * @param createdByUserId - The user ID performing the duplication
   * @returns The newly created organization-specific question
   */
  async duplicateQuestionForOrg(questionId: string, organizationId: string, createdByUserId: string): Promise<Question> {
    console.log(`[QUESTION] Attempting to duplicate question ${questionId} for organization ${organizationId}`);
    try {
      // First, check if this question has already been duplicated
      const { data: existingDuplicate, error: checkError } = await supabase
        .from('questions')
        .select('*')
        .eq('created_from_question_id', questionId)
        .eq('organization_id', organizationId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('[QUESTION] Error checking for existing duplicate:', checkError);
        throw new Error(`Failed to check for existing duplicate: ${checkError.message}`);
      }

      if (existingDuplicate) {
        console.log(`[QUESTION] Found existing duplicate: ${existingDuplicate.question_id}`);
        return mapQuestionDBToGraphQL(existingDuplicate);
      }

      // Fetch the global question
      const { data: globalQuestion, error: fetchError } = await supabase
        .from('questions')
        .select('*')
        .eq('question_id', questionId)
        .is('organization_id', null)
        .single();

      if (fetchError) {
        console.error('[QUESTION] Error fetching global question:', fetchError);
        throw new Error(`Failed to fetch global question: ${fetchError.message}`);
      }
      
      if (!globalQuestion) {
        console.error('[QUESTION] Global question not found:', questionId);
        throw new Error('Global question not found');
      }

      // Verify this is actually a global question
      if (!globalQuestion.is_global) {
        console.error('[QUESTION] Attempted to duplicate non-global question:', questionId);
        throw new Error('Cannot duplicate a non-global question');
      }

      console.log(`[QUESTION] Creating duplicate of question ${questionId}`);

      // Create the organization-specific copy
      const { data: newQuestion, error: insertError } = await supabase
        .from('questions')
        .insert({
          header: globalQuestion.header,
          format: globalQuestion.format,
          questions: globalQuestion.questions,
          response_schema: globalQuestion.response_schema,
          prompt_instructions: globalQuestion.prompt_instructions,
          organization_id: organizationId,
          created_from_question_id: questionId,
          is_global: false,
          created_by_user_id: createdByUserId
        })
        .select()
        .single();

      if (insertError) {
        console.error('[QUESTION] Error creating duplicate question:', insertError);
        throw new Error(`Failed to create duplicate question: ${insertError.message}`);
      }

      console.log(`[QUESTION] Successfully created duplicate question: ${newQuestion.question_id}`);
      return mapQuestionDBToGraphQL(newQuestion);
    } catch (error) {
      console.error('[QUESTION] Unexpected error in duplicateQuestionForOrg:', error);
      throw error;
    }
  },

  /**
   * Reset a customized question back to its global version
   * 
   * @param questionId - The ID of the customized question to reset
   * @returns The updated question
   */
  async resetCustomizedQuestion(questionId: string): Promise<Question> {
    console.log(`[QUESTION] Attempting to reset customized question ${questionId}`);
    
    try {
      // First, fetch the customized question to get its original global question
      const { data: customQuestion, error: fetchError } = await supabase
        .from('questions')
        .select('created_from_question_id, organization_id')
        .eq('question_id', questionId)
        .not('organization_id', 'is', null)
        .single();

      if (fetchError) {
        console.error('[QUESTION] Error fetching customized question:', fetchError);
        throw new Error(`Failed to fetch customized question: ${fetchError.message}`);
      }
      
      if (!customQuestion?.created_from_question_id) {
        console.error('[QUESTION] Question is not a customized version:', questionId);
        throw new Error('Question is not a customized version of a global question');
      }

      // Fetch the global question
      const { data: globalQuestion, error: globalFetchError } = await supabase
        .from('questions')
        .select('*')
        .eq('question_id', customQuestion.created_from_question_id)
        .single();

      if (globalFetchError) {
        console.error('[QUESTION] Error fetching global question:', globalFetchError);
        throw new Error(`Failed to fetch global question: ${globalFetchError.message}`);
      }
      
      if (!globalQuestion) {
        console.error('[QUESTION] Original global question not found:', customQuestion.created_from_question_id);
        throw new Error('Original global question not found');
      }

      console.log(`[QUESTION] Updating customized question ${questionId} with global content`);

      // Start a transaction to update both the question and any affected check-ins
      const { data: updatedQuestion, error: updateError } = await supabase
        .from('questions')
        .update({
          header: globalQuestion.header,
          format: globalQuestion.format,
          questions: globalQuestion.questions,
          response_schema: globalQuestion.response_schema,
          prompt_instructions: globalQuestion.prompt_instructions
        })
        .eq('question_id', questionId)
        .select()
        .single();

      if (updateError) {
        console.error('[QUESTION] Error updating customized question:', updateError);
        throw new Error(`Failed to update customized question: ${updateError.message}`);
      }

      // Find all check-ins using this question
      console.log(`[QUESTION] Finding check-ins using question ${questionId}`);
      const { data: checkIns, error: checkInsError } = await supabase
        .from('check_ins')
        .select('check_in_id, active_question_ids')
        .contains('active_question_ids', [questionId]);

      if (checkInsError) {
        console.error('[QUESTION] Error finding affected check-ins:', checkInsError);
        throw new Error(`Failed to find affected check-ins: ${checkInsError.message}`);
      }

      // Update each check-in's active_question_ids to use the global question instead
      if (checkIns && checkIns.length > 0) {
        console.log(`[QUESTION] Updating ${checkIns.length} check-ins to use global question`);
        
        const updates = checkIns.map(checkIn => ({
          check_in_id: checkIn.check_in_id,
          active_question_ids: checkIn.active_question_ids.map((id: string) => 
            id === questionId ? customQuestion.created_from_question_id : id
          )
        }));

        const { error: batchUpdateError } = await supabase
          .from('check_ins')
          .upsert(updates);

        if (batchUpdateError) {
          console.error('[QUESTION] Error updating check-ins:', batchUpdateError);
          // Log the error but don't throw since the question reset was successful
          console.warn('[QUESTION] Some check-ins may still reference the customized question');
        } else {
          console.log('[QUESTION] Successfully updated all affected check-ins');
        }
      } else {
        console.log('[QUESTION] No check-ins found using this question');
      }

      console.log(`[QUESTION] Successfully reset question ${questionId}`);
      return mapQuestionDBToGraphQL(updatedQuestion);
    } catch (error) {
      console.error('[QUESTION] Unexpected error in resetCustomizedQuestion:', error);
      throw error;
    }
  },

  /**
   * Get active question IDs for a template (ordered)
   * @param templateId - The template ID
   * @returns Array of question UUIDs (string[])
   */
  async getActiveQuestionIdsForTemplate(templateId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('templatequestions')
      .select('question_id')
      .eq('template_id', templateId)
      .order('display_order', { ascending: true });
    if (error) throw error;
    if (!data) return [];
    return data.map((row: any) => row.question_id);
  },

  /**
   * Get questions for a specific check-in
   * This queries active questions for the check-in's template
   * 
   * @param checkInId - The ID of the check-in
   * @returns Array of questions
   */
  async getQuestionsByCheckIn(checkInId: string): Promise<Question[]> {
    try {
      // First get the check-in to find its template_id and active_question_ids
      const { data: checkIn, error: checkInError } = await supabase
        .from('check_ins')
        .select('template_id, active_question_ids, organization_id')
        .eq('check_in_id', checkInId)
        .single();
      
      if (checkInError) throw checkInError;
      if (!checkIn) return [];
      
      // If check-in has active_question_ids, use those directly
      if (checkIn.active_question_ids && checkIn.active_question_ids.length > 0) {
        const { data: questions, error: questionsError } = await supabase
          .from('questions')
          .select('question_id, header, format, questions, response_schema, organization_id, created_from_question_id')
          .in('question_id', checkIn.active_question_ids);
        
        if (questionsError) throw questionsError;
        return questions ? questions.map(mapQuestionDBToGraphQL) : [];
      }
      
      // Otherwise, fall back to the template's questions
      if (!checkIn.template_id) return [];
      return this.getQuestionsByTemplate(checkIn.template_id);
    } catch (error) {
      console.error('Error in getQuestionsByCheckIn:', error);
      throw error;
    }
  },

  /**
   * Create a custom question and update check-in configuration
   * Uses database transaction to ensure consistency
   * 
   * @param checkInId - The ID of the check-in
   * @param organizationId - The organization ID
   * @param userId - The user ID creating the question
   * @returns The created question and timeline item data
   */
  async createCustomQuestion(checkInId: string, organizationId: string, userId: string): Promise<{
    question: Question,
    timelineItem: any
  }> {
    console.log(`[QUESTION] Creating custom question for check-in ${checkInId}`);
    
    try {
      const { data, error } = await supabase.rpc('create_custom_question_transaction', {
        p_check_in_id: checkInId,
        p_organization_id: organizationId,
        p_created_by_user_id: userId
      });

      if (error) {
        console.error('[QUESTION] Error creating custom question:', error);
        throw new Error(`Failed to create custom question: ${error.message}`);
      }

      if (!data?.success) {
        console.error('[QUESTION] Transaction failed:', data);
        throw new Error('Failed to create custom question');
      }

      console.log(`[QUESTION] Successfully created custom question: ${data.question_id}`);
      
      const question = mapQuestionDBToGraphQL(data.question);
      
      // Create timeline item data
      const timelineItem = {
        id: `question-${data.question_id}`,
        type: 'question' as const,
        title: question.header,
        order: 999, // Will be updated when timeline refreshes
        questionId: data.question_id,
        questionData: data.question
      };

      return { question, timelineItem };
    } catch (error) {
      console.error('[QUESTION] Unexpected error in createCustomQuestion:', error);
      throw error;
    }
  },

  /**
   * Save question changes with transaction safety
   * Updates header to match first question and syncs reports
   * 
   * @param questionId - The ID of the question to update
   * @param questionData - The updated question data
   * @param checkInId - The check-in ID for report syncing
   * @returns Promise that resolves when save is complete
   */
  async saveQuestionWithTransaction(
    questionId: string, 
    questionData: Partial<QuestionDB>,
    checkInId: string
  ): Promise<void> {
    console.log(`[QUESTION] Saving question ${questionId} with transaction`);
    
    try {
      // Validate main question
      const questions = questionData.questions;
      const mainQuestion = Array.isArray(questions) ? questions[0]?.trim() : '';
      
      if (!mainQuestion) {
        throw new Error('Main question is required');
      }

      // Update header to match first question
      const header = mainQuestion;
      const promptInstructions = questionData.prompt_instructions || null;

      const { data, error } = await supabase.rpc('save_question_transaction', {
        p_question_id: questionId,
        p_header: header,
        p_questions: questions,
        p_prompt_instructions: promptInstructions,
        p_check_in_id: checkInId
      });

      if (error) {
        console.error('[QUESTION] Error saving question:', error);
        throw new Error(`Failed to save question: ${error.message}`);
      }

      if (!data?.success) {
        console.error('[QUESTION] Save transaction failed:', data);
        throw new Error('Failed to save question');
      }

      console.log(`[QUESTION] Successfully saved question ${questionId}`);
    } catch (error) {
      console.error('[QUESTION] Unexpected error in saveQuestionWithTransaction:', error);
      throw error;
    }
  },

  /**
   * Check if a question can be removed from a check-in
   * 
   * @param questionId - The ID of the question to validate
   * @param checkInId - The ID of the check-in
   * @returns Validation result with details
   */
  async canRemoveQuestionFromCheckIn(questionId: string, checkInId: string): Promise<{
    canRemove: boolean;
    questionType: 'global' | 'custom';
    remainingQuestionsCount: number;
    pendingReportsCount: number;
    message: string;
  }> {
    console.log(`[QUESTION] Validating removal of question ${questionId} from check-in ${checkInId}`);
    
    try {
      const { data, error } = await supabase.rpc('can_remove_question_from_checkin', {
        p_question_id: questionId,
        p_check_in_id: checkInId
      });
      
      if (error) {
        console.error('[QUESTION] Error validating question removal:', error);
        throw new Error(`Failed to validate question removal: ${error.message}`);
      }
      
      return {
        canRemove: data.can_remove,
        questionType: data.question_type || 'global',
        remainingQuestionsCount: data.remaining_questions_count || 0,
        pendingReportsCount: data.pending_reports_count || 0,
        message: data.message || 'Validation completed'
      };
    } catch (error) {
      console.error('[QUESTION] Unexpected error in canRemoveQuestionFromCheckIn:', error);
      throw error;
    }
  },

  /**
   * Remove a question from a check-in with transaction safety
   * Updates pending reports and optionally deletes custom questions
   * 
   * @param questionId - The ID of the question to remove
   * @param checkInId - The ID of the check-in
   * @returns Result with operation details
   */
  async removeQuestionFromCheckIn(questionId: string, checkInId: string): Promise<{
    success: boolean;
    questionType: 'global' | 'custom';
    wasDeleted: boolean;
    affectedReportsCount: number;
    message: string;
  }> {
    console.log(`[QUESTION] Removing question ${questionId} from check-in ${checkInId}`);
    
    try {
      const { data, error } = await supabase.rpc('remove_question_from_checkin_transaction', {
        p_question_id: questionId,
        p_check_in_id: checkInId
      });

      if (error) {
        console.error('[QUESTION] Error removing question:', error);
        throw new Error(`Failed to remove question: ${error.message}`);
      }

      if (!data?.success) {
        console.error('[QUESTION] Remove transaction failed:', data);
        throw new Error(data?.message || 'Failed to remove question from check-in');
      }

      const wasDeleted = data.was_deleted || false;
      console.log(`[QUESTION] Successfully ${wasDeleted ? 'deleted' : 'removed'} ${data.question_type} question`);
      console.log(`[QUESTION] Updated ${data.affected_reports_count} pending reports`);
      
      return {
        success: true,
        questionType: data.question_type,
        wasDeleted,
        affectedReportsCount: data.affected_reports_count,
        message: data.message
      };
    } catch (error) {
      console.error('[QUESTION] Unexpected error in removeQuestionFromCheckIn:', error);
      throw error;
    }
  }
};

/**
 * Maps a Question from the database to the GraphQL type
 * Handles JSON parsing for text fields stored as JSON
 * 
 * @param data - Database record
 * @returns GraphQL Question type
 */
export function mapQuestionDBToGraphQL(data: QuestionDB): Question {
  function safeParse(val: any) {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  }
  return {
    id: data.question_id,
    header: data.header,
    format: data.format.toUpperCase() as any, // Convert to enum value
    questions: safeParse(data.questions),
    responseSchema: safeParse(data.response_schema),
    organizationId: data.organization_id || null,
    createdFromQuestionId: data.created_from_question_id || null,
    isCustomized: !!data.organization_id
  };
} 